import { buildCache } from "@/app/caching/buildCache";
import type { IHasItemsCache } from "@/app/caching/caches/itemsCache";
import type { IHasPackageTypesCache } from "@/app/caching/caches/packageTypesCache";
import type { IHasProductBrandsCache } from "@/app/caching/caches/productBrandsCache";
import type { IHasStockGroupsCache } from "@/app/caching/caches/stockGroupsCache";
import type { IHasSupplierManufacturersCache } from "@/app/caching/caches/supplierManufacturersCache";
import type { IHasUnitsOfMeasureCache } from "@/app/caching/caches/unitsOfMeasureCache";
import type { IHasVatCodesCache } from "@/app/caching/caches/vatCodesCache";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import {
  itemService,
  type Item,
  type PurchasingDetails,
} from "@/app/services/generated/itemService";
import type { EnumCurrency } from "@/app/services/generated/enums/enumCurrency.generated";
import type Guid from "@/app/types/common/guid";
import type { Maybe } from "@/components/utils/typeHelpers";
import tenantStore from "@/store/modules/tenantStore";
import type { StockBatch } from "@/app/services/generated/stockbatchService";
import { GuidSet } from "@/app/types/common/guidcollections";
import type { IHasPricingCategoriesCache } from "@/app/caching/caches/pricingCategoriesCache";

export function itemUom(item: Maybe<Item>, cache: IHasUnitsOfMeasureCache) {
  return cache.unitsOfMeasure.getById(item?.details.unitOfMeasureId) ?? null;
}

export function itemPackageType(
  item: Maybe<Item>,
  cache: IHasPackageTypesCache
) {
  return cache.packageTypes.getById(item?.packaging?.packageTypeId) ?? null;
}

export function itemPricingCategory(
  item: Maybe<Item>,
  cache: IHasPricingCategoriesCache
) {
  return (
    cache.pricingCategories.getById(item?.invoicing?.pricingCategoryId) ?? null
  );
}

/**
 * Matches backend Item.ResolveUnitLitresWithUomPriorityAsync:
 * UOM litres, else package type litres, else 0.
 */
export function resolveUnitLitresWithUomPriority(
  item: Maybe<Item>,
  cache: IHasUnitsOfMeasureCache & IHasPackageTypesCache
) {
  return (
    itemUom(item, cache)?.litres ?? itemPackageType(item, cache)?.litres ?? 0
  );
}

export function itemIdToUom(
  itemId: Maybe<Guid>,
  cache: IHasItemsCache & IHasUnitsOfMeasureCache
) {
  return itemUom(cache.items.getById(itemId), cache);
}

export function itemStockGroup(item: Maybe<Item>, cache: IHasStockGroupsCache) {
  return cache.stockGroups.getById(item?.stock?.stockGroupId);
}

export function itemManufacturer(
  item: Maybe<Item>,
  cache: IHasSupplierManufacturersCache
) {
  return cache.supplierManufacturers.getById(item?.alcoholDuty?.manufacturerId);
}

export function itemProductBrand(
  item: Maybe<Item>,
  cache: IHasProductBrandsCache
) {
  return cache.productBrands.getById(item?.invoicing?.productBrandId);
}

export function itemResolvedTastingNotes(
  item: Maybe<Item>,
  cache: IHasProductBrandsCache
) {
  return item?.labelling.labelUsesBrandTastingNotes
    ? itemProductBrand(item, cache)?.tastingNotes
    : item?.labelling.labelTastingNotes;
}

export function itemResolvedIngredientsAndAllergens(
  item: Maybe<Item>,
  cache: IHasProductBrandsCache
) {
  return item?.labelling.labelUsesBrandIngredientsAndAllergens
    ? itemProductBrand(item, cache)?.ingredientsAndAllergens
    : item?.labelling.labelIngredientsAndAllergens;
}

export function itemBestBeforeDate(item: Maybe<Item>) {
  const bestBeforeDays = item?.labelling.bestBeforeDays;
  return bestBeforeDays ? tenantService.today.plusDays(bestBeforeDays) : null;
}

export function itemIsTraceable(
  item: Maybe<Item>,
  cache: IHasPackageTypesCache
) {
  return (
    cache.packageTypes.getById(item?.packaging.packageTypeId)?.isTraceable ??
    false
  );
}

export function itemIsReturnable(
  item: Maybe<Item>,
  cache: IHasPackageTypesCache
) {
  return (
    cache.packageTypes.getById(item?.packaging.packageTypeId)?.isReturnable ??
    false
  );
}

export function itemDefaultSupplierId(item: Maybe<Item>) {
  return (
    item?.purchasingDetails.find((p: { isDefault: boolean }) => p.isDefault)
      ?.supplierId ?? null
  );
}

export function itemDefaultSupplier(
  item: Maybe<Item>,
  cache: IHasSupplierManufacturersCache
) {
  return (
    cache.supplierManufacturers.getById(itemDefaultSupplierId(item)) ?? null
  );
}

export function itemPurchasingDetailsForSupplierId(
  item: Maybe<Item>,
  supplierId: Maybe<Guid>
) {
  return (
    item?.purchasingDetails.find(
      (p) => supplierId && supplierId.equals(p.supplierId)
    ) ??
    item?.purchasingDetails.find((p) => !p.supplierId) ??
    null
  );
}

/**
 * Resolve purchasing details for a PO line.
 * Prefers the PO supplier's detail; otherwise uses default
 * details only when the PO currency matches the tenant currency.
 * Returns null when nothing is applicable in the correct currency.
 */
export function itemPurchasingDetailsForPurchaseOrder(
  item: Maybe<Item>,
  supplierId: Maybe<Guid>,
  currencyCode: EnumCurrency
) {
  const details = item?.purchasingDetails ?? [];

  const forPoSupplier = details.find(
    (p) => supplierId && supplierId.equals(p.supplierId)
  );
  if (forPoSupplier) {
    return forPoSupplier;
  }

  const defaultDetails = details.find((p) => p.isDefault) ?? null;
  const tenantCurrencyCode = tenantService.settings.currency.currencyCode;
  if (defaultDetails && currencyCode.value === tenantCurrencyCode) {
    return defaultDetails;
  }

  return null;
}

export function purchasingDetailsPricePerUnit(
  purchasingDetails: PurchasingDetails
) {
  return purchasingDetails?.purchasePrice && purchasingDetails?.purchasePackSize
    ? purchasingDetails?.purchasePrice / purchasingDetails?.purchasePackSize
    : null;
}

export function itemUsesHopAlpha(item: Maybe<Item>) {
  return (
    tenantStore.state.tenantSettings.usesHopAlpha &&
    !!item?.materialDetails?.defaultHopAlpha
  );
}

export function itemIdUsesHopAlpha(cache: IHasItemsCache, itemId: Maybe<Guid>) {
  return itemUsesHopAlpha(cache.items.getById(itemId));
}

export async function itemsApplyCostPriceUpdates(
  updates: readonly { itemId: Guid; newCostPrice: number }[]
) {
  if (updates.length > 0) {
    const cache = await buildCache().withItems().buildAndUpdate();

    const itemsToSave = updates.map((update) => {
      const toSave = cache.items.forId(update.itemId).clone();
      toSave.details.costPrice = update.newCostPrice;
      return toSave;
    });

    await Promise.all(itemsToSave.map((item) => itemService.saveItem(item)));
  }
}

export function itemVatCode(item: Maybe<Item>, cache: IHasVatCodesCache) {
  return cache.vatCodes.getById(item?.vat?.vatCodeId) ?? null;
}

export function hasBatchTrackedProduct(cache: IHasItemsCache) {
  return cache.items.all.some(
    (x) =>
      x.details.mainPurpose.isProduct() && (x.stock?.isBatchTracked ?? false)
  );
}

export function getBatchText(batch: StockBatch, availableStock: number) {
  const batchUrn = batch.urn.length > 0 ? batch.urn : "-";
  const beforeDate = batch.bestBeforeDate
    ? tenantService.formatDate(batch.bestBeforeDate)
    : "-";
  return batchUrn + " | " + beforeDate + " | " + availableStock;
}

export function allSaleableProducts(
  cache: IHasItemsCache & IHasProductBrandsCache
) {
  const salableBrandIds = new GuidSet(
    cache.productBrands.all.filter((x) => x.allowSales).map((pb) => pb.id)
  );
  return cache.items
    .products()
    .filter(
      (x) =>
        x.invoicing?.allowSales &&
        (!x.invoicing.productBrandId ||
          salableBrandIds.has(x.invoicing.productBrandId))
    );
}

export const itemExtracts = {
  nameCode: (item: Item) => [item.details.name, item.details.code],
  id: (item: Item) => item.details.id,
  isHidden: (item: Item) => item.details.isHidden,
};
